"use client";

import * as React from "react";
import { cn } from "../primitives/cn";

export type EntitlementMode = "contact-admin" | "upsell" | "hide";

export type EntitlementContextValue = {
  entitlements: Record<string, boolean | number | null>;
  /** Returns true when the feature is included / under limit. */
  included: (key: string) => boolean;
};

const EntitlementContext = React.createContext<EntitlementContextValue | null>(null);

export function EntitlementProvider({
  entitlements,
  children,
}: {
  entitlements: Record<string, boolean | number | null>;
  children: React.ReactNode;
}) {
  const value = React.useMemo<EntitlementContextValue>(
    () => ({
      entitlements,
      included: (key: string) => {
        if (!(key in entitlements)) return true;
        const v = entitlements[key];
        if (v === false) return false;
        if (typeof v === "number" && v <= 0) return false;
        return true;
      },
    }),
    [entitlements],
  );

  return (
    <EntitlementContext.Provider value={value}>{children}</EntitlementContext.Provider>
  );
}

export function useEntitlements() {
  const ctx = React.useContext(EntitlementContext);
  if (!ctx) {
    return {
      entitlements: {},
      included: () => true,
    } satisfies EntitlementContextValue;
  }
  return ctx;
}

/**
 * EntitlementGate — when not entitled, greys out + contact-admin (default for this product).
 * PermissionGate hides instead. Never write both as the same behavior.
 *
 * Default mode is `contact-admin` with English copy (apps pass locale strings).
 * Do not use upgrade-plan wording for standard-webapp.
 */
export function EntitlementGate({
  entitlement,
  mode = "contact-admin",
  children,
  contactAdminMessage = "Limit reached. Please contact an administrator.",
  upsellMessage = "Available after upgrade",
}: {
  entitlement: string;
  mode?: EntitlementMode;
  children: React.ReactNode;
  contactAdminMessage?: string;
  upsellMessage?: string;
}) {
  const { included } = useEntitlements();
  const ok = included(entitlement);

  if (ok) return <>{children}</>;
  if (mode === "hide") return null;

  const message = mode === "upsell" ? upsellMessage : contactAdminMessage;

  return (
    <div
      className={cn(
        "relative rounded-md",
        "after:absolute after:inset-0 after:rounded-md after:bg-background/60 after:content-['']",
      )}
      aria-disabled
      title={message}
    >
      <div className="pointer-events-none opacity-50">{children}</div>
      <p className="relative z-10 mt-2 text-xs text-muted-foreground">{message}</p>
    </div>
  );
}
